import React, { useState, useEffect } from 'react';
import { API_URL } from '../../config/api';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Building2, MapPin, Plus, Trash2, FileText, Pencil, Check, X, Mail } from 'lucide-react';
import { Button } from '../../components/ui/button';

const ClientDetail = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSiteForm, setShowSiteForm] = useState(false);
  const [siteForm, setSiteForm] = useState({ site_name: '', site_address: '' });
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    company_name: '',
    billing_contact_name: '',
    billing_email: '',
    invoice_cc_email: ''
  });
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  useEffect(() => {
    fetchClient();
  }, [clientId]);

  const fetchClient = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/clients/${clientId}`);
      const data = await res.json();
      setClient(data.client);
      setSites(data.sites || []);
      // Pre-fill edit form
      if (data.client) {
        setEditForm({
          company_name: data.client.company_name || '',
          billing_contact_name: data.client.billing_contact_name || '',
          billing_email: data.client.billing_email || '',
          invoice_cc_email: data.client.invoice_cc_email || ''
        });
      }
    } catch (err) {
      console.error('Failed to fetch client:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditSaving(true);
    setEditError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/clients/${clientId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm)
      });
      if (res.ok) {
        setEditing(false);
        fetchClient();
      } else {
        const data = await res.json();
        const errorMsg = Array.isArray(data.detail)
          ? data.detail.map(e => e.msg).join(', ')
          : (data.detail || 'Failed to update client');
        setEditError(errorMsg);
      }
    } catch (err) {
      console.error('Failed to update client:', err);
      setEditError('Connection error. Please try again.');
    } finally {
      setEditSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditing(false);
    setEditError('');
    if (client) {
      setEditForm({
        company_name: client.company_name || '',
        billing_contact_name: client.billing_contact_name || '',
        billing_email: client.billing_email || '',
        invoice_cc_email: client.invoice_cc_email || ''
      });
    }
  };

  const handleAddSite = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/sites`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...siteForm, client_id: clientId })
      });
      if (res.ok) {
        setSiteForm({ site_name: '', site_address: '' });
        setShowSiteForm(false);
        fetchClient();
      }
    } catch (err) {
      console.error('Failed to add site:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSite = async (siteId) => {
    if (!window.confirm('Delete this site?')) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/sites/${siteId}`, { method: 'DELETE' });
      if (res.ok) {
        fetchClient();
      } else {
        alert('Cannot delete site with active contracts');
      }
    } catch (err) {
      console.error('Failed to delete site:', err);
    }
  };

  if (loading) {
    return (
      <div className="p-6 lg:p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-48"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="p-6 lg:p-8">
        <p className="text-gray-500">Client not found</p>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-4xl">
      {/* Header */}
      <div className="mb-6">
        <button onClick={() => navigate('/admin/clients')} className="flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-4">
          <ArrowLeft className="w-4 h-4" />
          Back to Clients
        </button>
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-[#1a1f4e]/10 rounded-xl flex items-center justify-center">
            <Building2 className="w-6 h-6 text-[#1a1f4e]" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-semibold text-gray-900">{client.company_name}</h1>
            <p className="text-gray-500">{client.billing_contact_name} &middot; {client.billing_email}</p>
          </div>
          {!editing && (
            <Button
              variant="outline"
              onClick={() => setEditing(true)}
              className="h-9 px-4"
              data-testid="edit-client-btn"
            >
              <Pencil className="w-4 h-4 mr-2" />
              Edit
            </Button>
          )}
        </div>
      </div>

      {/* Edit Client Form */}
      {editing && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Edit Client Details</h2>
          {editError && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
              {editError}
            </div>
          )}
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
              <input
                type="text"
                required
                value={editForm.company_name}
                onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })}
                className="w-full h-11 px-4 rounded-lg border border-gray-200 focus:border-[#1a1f4e] focus:ring-2 focus:ring-[#1a1f4e]/10 outline-none"
                data-testid="edit-company-input"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Billing Contact Name *</label>
                <input
                  type="text"
                  required
                  value={editForm.billing_contact_name}
                  onChange={(e) => setEditForm({ ...editForm, billing_contact_name: e.target.value })}
                  className="w-full h-11 px-4 rounded-lg border border-gray-200 focus:border-[#1a1f4e] focus:ring-2 focus:ring-[#1a1f4e]/10 outline-none"
                  data-testid="edit-contact-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Billing Email *</label>
                <input
                  type="email"
                  required
                  value={editForm.billing_email}
                  onChange={(e) => setEditForm({ ...editForm, billing_email: e.target.value })}
                  className="w-full h-11 px-4 rounded-lg border border-gray-200 focus:border-[#1a1f4e] focus:ring-2 focus:ring-[#1a1f4e]/10 outline-none"
                  data-testid="edit-email-input"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <span className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-gray-400" />
                  Invoice CC Email
                </span>
              </label>
              <input
                type="email"
                value={editForm.invoice_cc_email}
                onChange={(e) => setEditForm({ ...editForm, invoice_cc_email: e.target.value })}
                placeholder="Someone to always CC on invoices (optional)"
                className="w-full h-11 px-4 rounded-lg border border-gray-200 focus:border-[#1a1f4e] focus:ring-2 focus:ring-[#1a1f4e]/10 outline-none"
                data-testid="edit-cc-email-input"
              />
              <p className="text-xs text-gray-500 mt-1">This person will receive a copy of every invoice sent to this client.</p>
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="submit" disabled={editSaving} className="bg-[#1a1f4e] hover:bg-[#2a2f5e]" data-testid="save-edit-btn">
                {editSaving ? 'Saving...' : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
              <Button type="button" variant="outline" onClick={handleCancelEdit}>
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* CC Email Badge (when not editing) */}
      {!editing && client.invoice_cc_email && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 flex items-center gap-3">
          <Mail className="w-5 h-5 text-blue-600" />
          <div>
            <p className="text-sm font-medium text-blue-900">Invoice CC</p>
            <p className="text-sm text-blue-700">{client.invoice_cc_email} receives a copy of all invoices</p>
          </div>
        </div>
      )}

      {/* Sites Section */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Sites / Blocks</h2>
          <Button size="sm" onClick={() => setShowSiteForm(!showSiteForm)} className="bg-[#1a1f4e] hover:bg-[#2a2f5e]" data-testid="add-site-btn">
            <Plus className="w-4 h-4 mr-1" />
            Add Site
          </Button>
        </div>

        {/* Add Site Form */}
        {showSiteForm && (
          <form onSubmit={handleAddSite} className="bg-gray-50 rounded-lg p-4 mb-4 space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Site Name *</label>
              <input
                type="text"
                required
                placeholder="e.g., Maple Court"
                value={siteForm.site_name}
                onChange={(e) => setSiteForm({ ...siteForm, site_name: e.target.value })}
                className="w-full h-10 px-3 rounded-lg border border-gray-200 focus:border-[#1a1f4e] outline-none text-sm"
                data-testid="site-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Site Address *</label>
              <input
                type="text"
                required
                placeholder="Full address"
                value={siteForm.site_address}
                onChange={(e) => setSiteForm({ ...siteForm, site_address: e.target.value })}
                className="w-full h-10 px-3 rounded-lg border border-gray-200 focus:border-[#1a1f4e] outline-none text-sm"
                data-testid="site-address-input"
              />
            </div>
            <div className="flex gap-2">
              <Button type="submit" size="sm" disabled={saving} className="bg-[#1a1f4e] hover:bg-[#2a2f5e]" data-testid="save-site-btn">
                {saving ? 'Saving...' : 'Save Site'}
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={() => setShowSiteForm(false)}>Cancel</Button>
            </div>
          </form>
        )}

        {/* Sites List */}
        {sites.length === 0 ? (
          <p className="text-gray-500 text-sm">No sites added yet.</p>
        ) : (
          <div className="space-y-2">
            {sites.map((site) => (
              <div key={site.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="font-medium text-sm">{site.site_name}</p>
                    <p className="text-xs text-gray-500">{site.site_address}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Link
                    to={`/admin/contracts/new?site=${site.id}`}
                    className="text-xs text-[#1a1f4e] hover:underline flex items-center gap-1"
                  >
                    <FileText className="w-3 h-3" />
                    Add Contract
                  </Link>
                  <button onClick={() => handleDeleteSite(site.id)} className="p-1 text-gray-400 hover:text-red-500">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientDetail;
